import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CreateNewClientPage from "../../../entities/SuperAdminPage/CreateNewClient";
import { subscriptionService } from "../../../api";
import { useAlert } from "../../../context/AlertContext";
import LoaderHelper from "../../../utils/Loader/LoaderHelper";
import dayjs from "dayjs";

const PAYSTACK_PAYMENT_CONTEXT_KEY = "pendingPaystackSubscriptionPayment";

const persistPendingPaymentContext = (authorizationUrl = "", fallbackReference = "") => {
  try {
    let reference = String(fallbackReference || "").trim();

    if (!reference && authorizationUrl) {
      const url = new URL(authorizationUrl);
      reference =
        String(
          url.searchParams.get("reference") || url.searchParams.get("trxref") || ""
        ).trim();
    }

    sessionStorage.setItem(
      PAYSTACK_PAYMENT_CONTEXT_KEY,
      JSON.stringify({
        reference,
        createdAt: Date.now(),
      })
    );
  } catch (error) {
    // Ignore storage parsing issues and continue the flow.
  }
};

const CreateNewClient = () => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const { showAlert } = useAlert();
  const isEditMode = !!id;

  useEffect(() => {
    if (isEditMode && id) {
      fetchSubscriptionData();
    }
  }, [isEditMode, id]);

  const fetchSubscriptionData = async () => {
    setFetching(true);
    LoaderHelper.loaderStatus(true);
    try {
      const result = await subscriptionService.getById(id);
      if (result && !result.error) {
        const normalizeId = (value) => {
          if (!value) return "";
          if (typeof value === "string") return value;
          return value._id || value.id || "";
        };

        const extractSubscriptionPayload = (res = {}) => {
          const responseData = res?.data;
          if (responseData?.subscription) {
            return {
              subscription: responseData.subscription,
              transactionHistory: responseData.transactionHistory,
            };
          }
          return {
            subscription: responseData || res,
            transactionHistory: responseData?.transactionHistory || res?.transactionHistory,
          };
        };

        const { subscription: subData } = extractSubscriptionPayload(result);
        const company = subData.companyId || {};
        const admin = subData.adminId || {};
        const planFromSub = subData?.planId && typeof subData.planId === "object" ? subData.planId : {};

        const companyId = normalizeId(subData.companyId);
        const adminId = normalizeId(subData.adminId);
        const planId = normalizeId(subData.planId);

        const loadedData = {
          companyId: companyId,
          adminId: adminId,
          plan: planId,
          startDate: subData.date ? dayjs(subData.date) : dayjs(),
          isActive: subData.isActive !== undefined ? subData.isActive : true,
          
          companyName: company.companyName || "",
          companyMailingAddress: company.email || "",
          address1: company.address1 || "",
          owner: company.owner || `${admin.firstName || ""} ${admin.lastName || ""}`.trim() || "",
          phoneNumber: company?.cellNumber?.toString() || company?.phone?.toString() || admin?.phone?.toString() || "",
          phoneCountryCode: company?.cellNumberCCode?.toString() || company?.phoneCCode?.toString() || admin?.cCode?.toString() || "27",
          city: company.city || "",
          state: company.state || "",
          zipCode: company.zipCode || "",
          country: company.country || "",
          industry: company.industry || "",
          billingCycle: planFromSub.billingCycle || subData?.billingCycle || "",
          adminEmail: admin.email || "",
          adminFirstName: admin.firstName || "",
          adminLastName: admin.lastName || "",
          planPrice: subData.planPrice || planFromSub.price || "",
          planCurrency: subData.planCurrency || "",
          planExpiryDate: (subData.planExpiryDate || subData.overrides?.planExpiryDate || subData.nextBillingDate) ? dayjs(subData.planExpiryDate || subData.overrides?.planExpiryDate || subData.nextBillingDate) : null,
          moduleAccessOverrides: subData.overrides?.moduleAccessOverrides || subData.moduleAccessOverrides || {},
          moduleLimitOverrides: subData.overrides?.moduleLimitOverrides || subData.moduleLimitOverrides || {},
          effectiveAccess: result.data?.effectiveAccess || {},
          effectiveLimits: result.data?.effectiveLimits || {},
          usage: result.data?.usage || {},
          moduleAccess: planFromSub.moduleAccess || {},
          moduleLimits: planFromSub.moduleLimits || {},
        };
        
        setSubscriptionData(loadedData);
      } else {
        showAlert(result?.message || "Failed to load subscription data", "error");
      }
      setFetching(false);
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      setFetching(false);
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message || "Error loading subscription data", "error");
    }
  };

  const handleCreateClient = async (formData) => {
    setLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const dateValue = formData.startDate
        ? (dayjs.isDayjs(formData.startDate) ? formData.startDate : dayjs(formData.startDate)).format("YYYY-MM-DD")
        : null;

      const storedCompanyId = localStorage.getItem("companyId") || "";
      const storedUserId =
        localStorage.getItem("userId") ||
        localStorage.getItem("employeeId") ||
        "";

      if (!localStorage.getItem("userId") && storedUserId) {
        localStorage.setItem("userId", storedUserId);
      }

      const companyId = String(storedCompanyId || formData.companyId || "").trim();
      const adminId = String(storedUserId || formData.adminId || "").trim();

      const mergeWithPriority = (base = {}, overrides = {}) => {
        const merged = { ...base };
        Object.keys(overrides).forEach((key) => {
          if (overrides[key] !== undefined && overrides[key] !== null) {
            merged[key] = overrides[key];
          }
        });
        delete merged._id;
        return merged;
      };

      const payload = {
        companyId: companyId,
        adminId: adminId,
        planId: formData.plan,
        date: dateValue,
        isActive: formData.isActive !== undefined ? formData.isActive : true,
        overrides: {
          moduleAccessOverrides: mergeWithPriority(formData.moduleAccess, formData.moduleAccessOverrides),
          moduleLimitOverrides: mergeWithPriority(formData.moduleLimits, formData.moduleLimitOverrides),
          planExpiryDate: formData.planExpiryDate
            ? (dayjs.isDayjs(formData.planExpiryDate) ? formData.planExpiryDate : dayjs(formData.planExpiryDate)).format("YYYY-MM-DD")
            : null,
        },
      };

      let result;
      if (isEditMode) {
        try {
          result = await subscriptionService.update(id, payload);
          
          if (formData.companyId) {
            let cCode = formData.phoneCountryCode || "27";
            let cNumber = formData.phoneNumber || "";

            const companyPayload = {
              companyName: formData.companyName,
              email: formData.companyMailingAddress,
              address1: formData.address1,
              owner: formData.owner,
              phone: cNumber,
              cellNumber: cNumber,
              cellNumberCCode: cCode,
              city: formData.city,
              state: formData.state,
              zipCode: formData.zipCode,
              country: formData.country,
              industry: formData.industry,
            };
            await subscriptionService.updateCompany(formData.companyId, companyPayload);
          }

          if (formData.adminId) {
            const adminPayload = {
              email: formData.adminEmail,
            };
            // Try to extract first and last name from owner if possible
            if (formData.owner && formData.owner.includes(" ")) {
              const nameParts = formData.owner.split(" ");
              adminPayload.firstName = nameParts[0];
              adminPayload.lastName = nameParts.slice(1).join(" ");
            } else if (formData.owner) {
              adminPayload.firstName = formData.owner;
              adminPayload.lastName = formData.adminLastName || "";
            }
            await subscriptionService.updateAdmin(formData.adminId, adminPayload);
          }

          if (result?.success !== false) {
            showAlert(result?.message || "Client updated successfully", "success");
            navigate("/dashboard/userManagement");
          } else {
            showAlert(result?.message || "Failed to update subscription", "error");
          }
        } catch (e) {
          showAlert(e?.message || "Failed to update client details", "error");
        }
      } else {
        result = await subscriptionService.create(payload);
        if (result?.success !== false) {
          const authorizationUrl = result?.data?.authorizationUrl;
          const paymentReference =
            result?.data?.reference ||
            result?.data?.trxref ||
            result?.reference ||
            result?.trxref ||
            "";
          showAlert(result?.message || "Subscription created successfully", "success");
          if (authorizationUrl) {
            persistPendingPaymentContext(authorizationUrl, paymentReference);
            window.location.href = authorizationUrl;
          } else {
            navigate("/dashboard/userManagement");
          }
        } else {
          showAlert(result?.message || "Failed to create subscription", "error");
        }
      }
      setLoading(false);
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      setLoading(false);
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message || `Error ${isEditMode ? "updating" : "creating"} subscription`, "error");
    }
  };

  return (
    <CreateNewClientPage
      loading={loading}
      fetching={fetching}
      handleCreateClient={handleCreateClient}
      isEditMode={isEditMode}
      subscriptionId={id}
      initialData={subscriptionData}
    />
  );
};

export default CreateNewClient;
